import { HttpContextToken, HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, map, share } from 'rxjs/operators';

/**
 * Opt a single GET out of in-flight de-duplication — for a caller that must see its own round trip
 * (e.g. a poll that re-reads right after a write it just issued).
 */
export const SKIP_DEDUPE = new HttpContextToken<boolean>(() => false);

/** The GETs currently on the wire, by request key. Holds nothing once a request has settled. */
@Injectable({ providedIn: 'root' })
export class InflightGetRequests {
  readonly byKey = new Map<string, Observable<HttpEvent<unknown>>>();
}

const requestKey = (req: HttpRequest<unknown>) =>
  JSON.stringify([
    req.urlWithParams,
    req.responseType,
    req.withCredentials,
    req.headers
      .keys()
      .sort()
      .map((name) => [name, req.headers.getAll(name)]),
  ]);

/**
 * A joiner gets its own copy of a JSON body: before de-duplication every caller received a fresh
 * object, and callers do mutate what they get (forms bind to it) — sharing one instance would leak
 * one component's edits into another.
 */
const copyForJoiner = (event: HttpEvent<unknown>) =>
  event instanceof HttpResponse &&
  event.body !== null &&
  typeof event.body === 'object' &&
  !(event.body instanceof Blob)
    ? event.clone({ body: structuredClone(event.body) })
    : event;

/**
 * Collapses identical GETs that are in flight at the same time into ONE request.
 *
 * Page loads fired the same read from several independent callers within milliseconds — side-nav
 * and tour service both load `api/user/profile`, several components load `api/company/all` (up to
 * four times on `/`, 0.7–2.7 s each on dev), the chain list's facets twice. Every one of those is a
 * full server round trip for an answer the first caller is already waiting for.
 *
 * Deliberately NOT a cache: the entry is dropped the moment the request settles, so a GET issued
 * after that — e.g. a re-read after a PUT — always reaches the server. Must stay FIRST in the
 * interceptor chain so the key is built from the caller's own headers and the downstream
 * interceptors (loader, ux replay) see the one real request once. The joiner shares the leader's
 * HttpContext for the downstream interceptors (SKIP_LOADER included) — acceptable, it is the same
 * request.
 */
export const dedupeGetInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET' || req.reportProgress || req.context.get(SKIP_DEDUPE)) {
    return next(req);
  }
  const inflight = inject(InflightGetRequests).byKey;
  const key = requestKey(req);
  const running = inflight.get(key);
  if (running) {
    return running.pipe(map(copyForJoiner));
  }
  const shared = next(req).pipe(
    finalize(() => {
      if (inflight.get(key) === shared) inflight.delete(key);
    }),
    share()
  );
  inflight.set(key, shared);
  return shared;
};
